/**
 * Category filter and search, both folded out of the one list response (A4).
 *
 * D9: the filter sits at the top of the rail, search above the categories, and
 * the categories are toggle buttons rather than tabs (see the render below).
 *
 * NO SERVER CALL HAPPENS HERE, and none should. The population is the code-
 * defined registry — a small, complete catalogue identical for every
 * organisation — so the whole of it is already in the browser by the time this
 * renders. A `?category=` request would ask the server to re-send a subset of
 * what the caller is holding, and a count endpoint would be a second tally free
 * to disagree with the rows underneath it.
 *
 * ONLY CATEGORIES THAT HAVE ITEMS GET A TAB. The product vision names Bexio,
 * Aufgaben and System alongside E-Mails; the registry answers with `email` and
 * nothing else. An empty "Bexio (0)" tab is navigation that leads nowhere and a
 * promise the backend has not made — when the aggregator lands, its items bring
 * their own tab with them, automatically, because this counts what it is given.
 *
 * NO SEPARATE "Bereich" DROPDOWN. The registry exposes exactly one grouping
 * dimension, `category`, and these tabs already are it. A second control over
 * the same field would be two ways to say one thing, which is how filters start
 * disagreeing.
 *
 * SEARCH IS SYNCHRONOUS AND UNDEBOUNCED. Debouncing exists to spare the network
 * and the server; there is no request behind this box. At this size the filter
 * is a handful of string comparisons per keystroke, and a delay would only make
 * typing feel broken.
 */
import React from "react";
import { Search } from "lucide-react";

import { automationCategoryLabel } from "./automationLabels";

export const ALL_CATEGORIES = "__all__";

/**
 * `[{ value, label, count }]`, "Alle" first and the rest in first-seen order.
 *
 * First-seen order is the registry's own presentation order, which is stable
 * across requests and organisations. Sorting alphabetically would reorder the
 * tabs the day somebody renames a German label.
 */
export function buildCategoryTabs(items) {
  const list = Array.isArray(items) ? items : [];
  const counts = new Map();
  list.forEach((item) => {
    const key = String(item?.category ?? "");
    counts.set(key, (counts.get(key) || 0) + 1);
  });
  return [
    { value: ALL_CATEGORIES, label: "Alle", count: list.length },
    ...Array.from(counts.entries()).map(([value, count]) => ({
      value,
      label: automationCategoryLabel(value),
      count,
    })),
  ];
}

/**
 * The visible rows for a category and a search term.
 *
 * Exported and pure so the filter rule can be asserted without rendering an
 * input and typing into it. Search covers label, description, key and category
 * — the four fields a reader could plausibly remember an automation by.
 */
export function filterAutomations(items, category, query) {
  const list = Array.isArray(items) ? items : [];
  const needle = String(query || "").trim().toLowerCase();
  return list.filter((item) => {
    if (category !== ALL_CATEGORIES && String(item?.category ?? "") !== category) {
      return false;
    }
    if (!needle) return true;
    return [item?.label, item?.description, item?.key, item?.category]
      .filter(Boolean)
      .some((field) => String(field).toLowerCase().includes(needle));
  });
}

export default function AutomationFilters({
  tabs,
  category,
  onCategoryChange,
  query,
  onQueryChange,
}) {
  return (
    <div className="flex flex-col gap-3">
      <div className="relative">
        <Search
          className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
          aria-hidden="true"
        />
        <input
          type="search"
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          placeholder="Automatisierung suchen …"
          aria-label="Automatisierung suchen"
          className="w-full rounded-[10px] border border-border bg-background py-2 pl-9 pr-3 text-[12.5px] text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        />
      </div>

      {/* FILTER BUTTONS, NOT TABS (D9). These narrow a list; they have no panel
          of their own. Since D9 the page has real tabs — the workspace's — and
          a second `tablist` without tabpanels would announce a structure that
          is not there. A group of toggle buttons with `aria-pressed` is what a
          single-choice filter is. */}
      <div
        role="group"
        aria-label="Kategorien"
        className="flex flex-wrap items-center gap-1.5"
      >
        {tabs.map((tab) => {
          const isActive = tab.value === category;
          return (
            <button
              key={tab.value}
              type="button"
              aria-pressed={isActive}
              onClick={() => onCategoryChange(tab.value)}
              className={[
                "rounded-full border px-3 py-1 text-[12px] font-semibold transition-colors max-md:min-h-9",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
                isActive
                  ? "border-primary bg-primary text-primary-foreground shadow-sm"
                  : "border-border bg-card text-muted-foreground hover:border-primary/30 hover:text-foreground",
              ].join(" ")}
            >
              {tab.label} ({tab.count})
            </button>
          );
        })}
      </div>
    </div>
  );
}
